import { mergeRefs } from "@schema-benchmarks/utils/react";
import { createLink } from "@tanstack/react-router";
import type { ComponentPropsWithRef } from "react";
import bem from "react-bem-helper";
import type { HapticPattern } from "web-haptics";
import { useWebHaptics } from "web-haptics/react";

import { classed } from "#src/shared/components/utils";
import { useFocusGroup } from "#src/shared/hooks/use-focus-group";

const collectionCls = bem("chip-collection");

export function ChipCollection({
  children,
  className,
  ref,
  ...props
}: ComponentPropsWithRef<"ul">) {
  const group = useFocusGroup();
  return (
    <ul {...props} {...collectionCls({ extra: className })} ref={mergeRefs(ref, group)}>
      {children}
    </ul>
  );
}

const cls = bem("chip");

export const DisplayChip = classed.li(cls({ modifier: "display" }).className);

export interface ChipProps extends ComponentPropsWithRef<"button"> {
  activated?: boolean;
  haptic?: true | HapticPattern;
}

export function Chip({ children, className, activated, haptic, onClick, ...props }: ChipProps) {
  const haptics = useWebHaptics();
  return (
    <li {...cls({ extra: className })}>
      <button
        type="button"
        {...props}
        aria-pressed={activated}
        {...cls({ element: "button" })}
        onClick={(event) => {
          if (haptic) void haptics.trigger(typeof haptic === "boolean" ? undefined : haptic);
          onClick?.(event);
        }}
      >
        {children}
      </button>
    </li>
  );
}

export interface LinkChipProps extends ComponentPropsWithRef<"a"> {
  haptic?: true | HapticPattern;
}

function BaseLinkChip({ children, className, haptic, onClick, ...props }: LinkChipProps) {
  const haptics = useWebHaptics();
  return (
    <li {...cls({ extra: className })}>
      {/* oxlint-disable-next-line jsx_a11y/click-events-have-key-events, jsx_a11y/no-static-element-interactions */}
      <a
        {...props}
        {...cls({ element: "button" })}
        onClick={(event) => {
          if (haptic) void haptics.trigger(typeof haptic === "boolean" ? undefined : haptic);
          onClick?.(event);
        }}
      >
        {children}
      </a>
    </li>
  );
}

export const LinkChip = createLink(BaseLinkChip);
